/**
 * 課金（store.ts）と広告（ads.ts）の遅延読み込みの窓口（しくみは lazy.ts の冒頭）。
 * この 2 つは「クリックの中で窓を開く」「起動直後の同意を聞く」「セッション開始からの時間」など、
 * 読む前から守る約束があるので、lazy.ts の汎用の窓口とは別にここで丁寧に扱う。
 */

import { KumoError, type KumoCore } from './core.js';
import { Lazy, loadChunk } from './lazy.js';
import { browserEnv, popupBlocked, type StoreEnv } from './storeEnv.js';
import { listenParentConsent } from './adsConsent.js';
import type { KumoStore } from './store.js';
import type { AdAdapter, ConsentInput, KumoAds } from './ads.js';

// ---- 課金 ----

export function lazyStore(core: KumoCore): KumoStore {
  // ネイティブのストア課金のプラグインがあるか（nativeStore.ts の createNativePurchases が null でない条件と同じ）
  const hasNativePurchases = () => !!core.native?.plugin('KumoPurchases');

  // 窓口が先に開いた決済の窓。本体（createStore）の purchase は最初に env.openWindow() を同期で呼ぶので、そこで渡す
  let preopened: Window | null = null;
  const base = browserEnv();
  const env: StoreEnv = {
    ...base,
    openWindow: () => {
      const w = preopened;
      preopened = null;
      return w ?? base.openWindow();
    }
  };
  const l = new Lazy(() => loadChunk('store', () => import('./store.js'), core.apiUrl).then((m) => m.createStore(core, env)));

  return {
    on: ((event: never, fn: never) => l.on((s) => s.on(event, fn))) as KumoStore['on'],
    // 読む前 = products() をまだ呼んでいない（本体も products() までは null）
    get mode() {
      return l.value ? l.value.mode : null;
    },
    products: (opts) => l.call((s) => s.products(opts)),
    purchase(productKey, opts = {}) {
      if (l.value) return l.value.purchase(productKey, opts);
      // Web のポップアップ決済: 窓は**今**（クリックのハンドラの中で）開く。本体を読んでからでは止められる（storeEnv.ts）。
      // ネイティブ（ストア課金・OS のブラウザ）とリダイレクト方式は窓を開かないので、読んでから呼べばよい
      if (hasNativePurchases() || opts.mode === 'redirect') return l.call((s) => s.purchase(productKey, opts));
      const win = base.openWindow();
      if (!win) return Promise.reject(popupBlocked());
      return l
        .call((s) => {
          preopened = win;
          try {
            return s.purchase(productKey, opts);
          } finally {
            preopened = null;
          }
        })
        .catch((e: unknown) => {
          // 本体を読めなかった（通信）: 空の窓を残さない。本体が窓を受け取った後の失敗は本体が閉じる
          if (e instanceof KumoError && e.code === 'sdk_load_failed') win.close();
          throw e;
        });
    },
    resumePendingPurchase: (opts) => l.call((s) => s.resumePendingPurchase(opts)),
    get: (purchaseId, lang) => l.call((s) => s.get(purchaseId, lang)),
    get channel() {
      if (l.value) return l.value.channel;
      return hasNativePurchases() ? 'store' : 'web';
    },
    webPurchaseAvailable: () => l.call((s) => s.webPurchaseAvailable()),
    recoverUnfinished: () => l.call((s) => s.recoverUnfinished()),
    entitlements: () => l.call((s) => s.entitlements()),
    history: (opts) => l.call((s) => s.history(opts))
  };
}

// ---- 広告 ----

export function lazyAds(core: KumoCore): KumoAds {
  // 読む前の状態は窓口が持ち、読んだ瞬間に本体へ渡す（読まないゲームでは、この数個の値だけで済む）
  const sessionStart = Date.now();
  const adapters = new Map<string, AdAdapter>();
  let consent: ConsentInput | null = null;
  let inGameplay = false;

  const l = new Lazy<KumoAds>(() => {
    // 読み始めた時点の状態で作る。読んでいる途中の呼び出しは Lazy の列に並び、順番どおりに後から流れる
    const snap = { adapters: [...adapters.values()], consent, inGameplay };
    return loadChunk('ads', () => import('./ads.js'), core.apiUrl).then((m) => {
      const ads = m.createAds(core, { adapters: snap.adapters, sessionStart, listenConsent: false });
      if (snap.consent) ads.setConsent(snap.consent);
      if (snap.inGameplay) ads.gameplayStart();
      return ads;
    });
  });

  // 同意は起動直後に親のページから 1 回だけ届く = 本体を読む前から聞く（adsConsent.ts）
  listenParentConsent((c) => facade.setConsent(c));

  // 失敗しない約束の API（rewarded / interstitial は通信エラーを reason で返し、例外にしない）を、読み込みの失敗でも守る
  const loadFailed = (e: unknown) => e instanceof KumoError && e.code === 'sdk_load_failed';

  const facade: KumoAds = {
    on: ((event: never, fn: never) => l.on((a) => a.on(event, fn))) as KumoAds['on'],
    registerAdapter(adapter) {
      adapters.set(adapter.name, adapter);
      l.ifStarted((a) => a.registerAdapter(adapter));
    },
    setConsent(c) {
      consent = { ...c };
      l.ifStarted((a) => a.setConsent(c));
    },
    gameplayStart() {
      inGameplay = true;
      l.ifStarted((a) => a.gameplayStart());
    },
    gameplayStop() {
      inGameplay = false;
      l.ifStarted((a) => a.gameplayStop());
    },
    get inGameplay() {
      return l.value ? l.value.inGameplay : inGameplay;
    },
    rewarded: (opts) =>
      l.call((a) => a.rewarded(opts)).catch((e: unknown) => {
        if (loadFailed(e)) return { granted: false as const, reward: [], reason: 'error' as const };
        throw e;
      }),
    interstitial: (opts) =>
      l.call((a) => a.interstitial(opts)).catch((e: unknown) => {
        if (loadFailed(e)) return { shown: false as const, reason: 'error' as const };
        throw e;
      })
  };
  return facade;
}
